import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SourceIdentifier } from '@ais-ui/api-spec/coverage';
import { useGetCoverageFunction, useGetSourceIdentifiers } from '@ais-ui/api';
import { DateRange } from 'react-day-picker';
import { transform } from 'ol/proj';
import { useToast } from '@ais-ui/ui-core';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  AddLayer,
  shapeToVectorLayer,
  useProjectionContext,
} from '@ais-ui/react-openlayers';
import { IntervalItem, LegendConfig } from '@ais-ui/websockets';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';

interface CoverageContextProps {
  children: ReactNode;
}

interface CoverageContextValue {
  selectedSourceIdentifiers: SourceIdentifier[];
  setSelectedSourceIdentifiers: Dispatch<SetStateAction<SourceIdentifier[]>>;
  onSourceIdentifierSelect: (
    sourceIdentifier: SourceIdentifier
  ) => Promise<void>;
  coverageDate: DateRange | undefined;
  setCoverageDate: Dispatch<SetStateAction<DateRange | undefined>>;
  coverageOverlays: Record<
    number,
    {
      base64: string;
      imageExtent: number[];
    }
  >;
  addSourceIdentifiersToMap: (
    addLayer: AddLayer,
    sourceIdentifiers: SourceIdentifier[],
    displayProjection: string,
    dataProjection: string
  ) => void;
  setSourceIdentifiersAsSelectedOnStartup: (
    sourceIdentifiers: SourceIdentifier[]
  ) => void;
  // Expose all source identifiers and helper functions
  allSourceIdentifiers: SourceIdentifier[];
  isLoadingAllSourceIdentifiers: boolean;
  findSourceIdentifierById: (id: number) => SourceIdentifier | undefined;
  findSourceIdentifierByMmsi: (mmsi: string) => SourceIdentifier | undefined;
  coverageLegend: LegendConfig<'interval'>;
  showCoverageLegend: boolean;
  loadingSourceIdentifiers: Set<number>;
  isSourceIdentifierLoading: (sourceIdentifierId: number) => boolean;
  isLoadingPeriodNavigation: boolean;
  // Period navigation functions
  navigateToNextPeriod: () => void;
  navigateToPreviousPeriod: () => void;
  getCurrentPeriodDelta: () => number;
  getNextPeriodDateRange: () => DateRange | undefined;
  getPreviousPeriodDateRange: () => DateRange | undefined;
  wouldNextPeriodBeInFuture: () => boolean;
}

// Helper function to parse WKT Point
const parsePoint = (wkt: string): [number, number] => {
  // Expected format: 'POINT (longitude latitude)'
  const match = wkt.match(
    /^POINT\s*\(\s*(-?\d+(\.\d+)?)\s+(-?\d+(\.\d+)?)\s*\)$/
  );

  if (!match) {
    throw new Error(`Invalid WKT Point format: "${wkt}"`);
  }

  const longitude = parseFloat(match[1]);
  const latitude = parseFloat(match[3]);

  return [longitude, latitude];
};

export const createLegend = (): LegendConfig<'interval'> => {
  const intervals = [
    { start: 0, end: 5, color: '#ff4000' },
    { start: 5, end: 10, color: '#ff6e00' },
    { start: 10, end: 15, color: '#ff9100' },
    { start: 15, end: 20, color: '#ffa600' },
    { start: 20, end: 25, color: '#ffb700' },
    { start: 30, end: 35, color: '#ffc400' },
    { start: 35, end: 40, color: '#ffd100' },
    { start: 40, end: 45, color: '#ffe600' },
    { start: 45, end: 50, color: '#ffee00' },
    { start: 50, end: 55, color: '#fff700' },
    { start: 55, end: 60, color: '#ffff00' },
    { start: 60, end: 65, color: '#f3ff00' },
    { start: 65, end: 70, color: '#eaff00' },
    { start: 70, end: 75, color: '#ddff00' },
    { start: 75, end: 80, color: '#d1ff00' },
    { start: 80, end: 85, color: '#bfff00' },
    { start: 85, end: 90, color: '#aaff00' },
    { start: 90, end: 95, color: '#88ff00' },
    { start: 95, end: 100, color: '#55ff00' },
  ];

  const items = intervals.map((interval) => ({
    color: interval.color,
    start_interval: interval.start,
    end_interval: interval.end,
    text: `${interval.start}% - ${interval.end}%`,
  }));

  const legend: LegendConfig<'interval'> = {
    type: 'interval',
    metric: 'speed',
    unit: 'percentage',
    title: 'AIS Signal Coverage',
    items: items as IntervalItem[],
  };

  return legend;
};

const CoverageContext = createContext<CoverageContextValue | undefined>(
  undefined
);

export const CoverageProvider = ({ children }: CoverageContextProps) => {
  const { displayProjection, dataProjection } = useProjectionContext();
  const [selectedSourceIdentifiers, setSelectedSourceIdentifiers] = useState<
    SourceIdentifier[]
  >([]);
  const { toast } = useToast();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const permissions = usePermissions();
  const { messages } = useLocalization();

  // Use refs for potentially unstable functions to avoid dependency issues
  const toastRef = React.useRef(toast);
  const formatDateTimeWithTimeZoneRef = React.useRef(formatDateTimeWithTimeZone);
  const messagesRef = React.useRef(messages);
  
  // Use refs for projection values to prevent them from causing re-renders
  const displayProjectionRef = React.useRef(displayProjection);
  const dataProjectionRef = React.useRef(dataProjection);

  // Keep refs updated
  React.useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  React.useEffect(() => {
    formatDateTimeWithTimeZoneRef.current = formatDateTimeWithTimeZone;
  }, [formatDateTimeWithTimeZone]);

  React.useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  React.useEffect(() => {
    displayProjectionRef.current = displayProjection;
  }, [displayProjection]);

  React.useEffect(() => {
    dataProjectionRef.current = dataProjection;
  }, [dataProjection]);

  // Always load all source identifiers in the coverage context
  // This ensures they're available for zoom functionality regardless of layer visibility
  const {
    data: allSourceIdentifiers,
    isLoading: isLoadingAllSourceIdentifiers,
  } = useGetSourceIdentifiers(permissions.coverage);

  const sourceIdentifiers = useMemo(
    () => allSourceIdentifiers || [],
    [allSourceIdentifiers]
  );

  const [coverageDate, setCoverageDate] = React.useState<DateRange | undefined>(
    {
      from: new Date(new Date().getTime() - 48 * 60 * 60 * 1000),
      to: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
    }
  );

  // Create dynamic coverage legend that includes the period as subtitle
  const coverageLegend = useMemo<LegendConfig<'interval'>>(() => {
    const baseLegend = createLegend();

    // Format the period subtitle if coverageDate is available
    let subtitle: string | undefined;
    if (coverageDate?.from && coverageDate?.to) {
      const fromStr = formatDateTimeWithTimeZoneRef.current(coverageDate.from, true);
      const toStr = formatDateTimeWithTimeZoneRef.current(coverageDate.to, true);
      subtitle = `${fromStr} to ${toStr}`;
    }

    return {
      ...baseLegend,
      subtitle,
    };
  }, [coverageDate]);

  // Track which source identifiers are currently being processed
  const [loadingSourceIdentifiers, setLoadingSourceIdentifiers] = useState<
    Set<number>
  >(new Set());

  // Track loading state for period navigation - use a ref to avoid triggering re-renders
  const [isLoadingPeriodNavigation, setIsLoadingPeriodNavigation] =
    useState(false);
  const isLoadingPeriodNavigationRef = React.useRef(isLoadingPeriodNavigation);
  
  // Keep ref in sync but don't trigger effects when it changes
  React.useEffect(() => {
    isLoadingPeriodNavigationRef.current = isLoadingPeriodNavigation;
  }, [isLoadingPeriodNavigation]);

  // Track periods for which we've already shown the "no data" warning to prevent spam
  const [warnedPeriods, setWarnedPeriods] = useState<Set<string>>(new Set());

  const [coverageOverlays, setCoverageOverlays] = useState<
    Record<
      number,
      {
        base64: string;
        imageExtent: number[];
      }
    >
  >({});

  const showCoverageLegend = useMemo(
    () => selectedSourceIdentifiers.length > 0,
    [selectedSourceIdentifiers]
  );

  const { getCoverage } = useGetCoverageFunction();

  // Helper function to check if view box coordinates represent empty coverage
  const isEmptyCoverage = useCallback((viewBoxCoordinates: string): boolean => {
    // Check if all coordinates are zero, indicating no coverage data
    const regex = /\((-?\d+\.\d+)\s(-?\d+\.\d+)\)/g;
    const coordinates: number[] = [];
    let match;

    while ((match = regex.exec(viewBoxCoordinates)) !== null) {
      const longitude = parseFloat(match[1]);
      const latitude = parseFloat(match[2]);
      coordinates.push(longitude, latitude);
    }

    // If we have coordinates but they are all zero, it's empty coverage
    return coordinates.length > 0 && coordinates.every((coord) => coord === 0);
  }, []);

  // Helper function to format date range for display using proper date formatting with time
  // Use a ref-based approach to ensure this is truly stable
  const formatDateRangeRef = React.useRef<(dateRange: DateRange) => string>(undefined!);
  
  if (!formatDateRangeRef.current) {
    formatDateRangeRef.current = (dateRange: DateRange): string => {
      if (!dateRange.from || !dateRange.to) return 'in the selected period';

      const fromStr = formatDateTimeWithTimeZoneRef.current(dateRange.from, true);
      const toStr = formatDateTimeWithTimeZoneRef.current(dateRange.to, true);
      return `from ${fromStr} to ${toStr}`;
    };
  }
  
  const formatDateRange = formatDateRangeRef.current;

  const onSourceIdentifierSelect = useCallback(
    async (sourceIdentifier: SourceIdentifier) => {
      // Check if user has coverage permissions before proceeding
      if (!permissions.coverage) {
        toastRef.current({
          title: 'Access Denied',
          description: 'You do not have permission to access coverage data.',
          variant: 'destructive',
        });
        return;
      }

      // Use functional update to access current state without depending on it
      let shouldProceed = true;
      setSelectedSourceIdentifiers((prevSelected) => {
        const exists = prevSelected.some(
          (selected) => selected.id === sourceIdentifier.id
        );

        if (exists) {
          // Remove from selection - this allows deselecting by clicking on selected source identifiers
          const sourceIdentifierName =
            sourceIdentifier.pssMmsi !== -1
              ? sourceIdentifier.pssMmsi.toString()
              : sourceIdentifier.sourceTag;

          toastRef.current({
            title: 'Success!',
            description: `Unselected source identifier ${sourceIdentifierName} and removing its coverage from the map.`,
          });
          
          shouldProceed = false;
          return prevSelected.filter((selected) => selected.id !== sourceIdentifier.id);
        }
        
        return prevSelected;
      });

      if (!shouldProceed) {
        return;
      }

      // Check if already in loading state to prevent double selection
      let isAlreadyLoading = false;
      setLoadingSourceIdentifiers((prev) => {
        if (prev.has(sourceIdentifier.id)) {
          isAlreadyLoading = true;
          return prev;
        }
        return prev;
      });

      if (isAlreadyLoading) {
        const sourceIdentifierName =
          sourceIdentifier.pssMmsi !== -1
            ? sourceIdentifier.pssMmsi.toString()
            : sourceIdentifier.sourceTag;

        toastRef.current({
          title: 'Already Loading',
          description: `Source identifier ${sourceIdentifierName} is already being loaded.`,
          variant: 'default',
        });
        return;
      }

      // Check if coverage data exists before adding
      if (!coverageDate) {
        toastRef.current({
          title: messagesRef.current.coverageNoDateRange,
          description: 'Please select a date range to view coverage data.',
          variant: 'default',
        });
        return;
      }

      const sourceIdentifierName =
        sourceIdentifier.pssMmsi !== -1
          ? sourceIdentifier.pssMmsi.toString()
          : sourceIdentifier.sourceTag;

      // Show loading toast immediately
      toastRef.current({
        title: 'Loading Coverage',
        description: `Loading coverage data for source identifier ${sourceIdentifierName}...`,
        variant: 'default',
      });

      // Add to loading state
      setLoadingSourceIdentifiers((prev) =>
        new Set(prev).add(sourceIdentifier.id)
      );

      try {
        // Check coverage data first
        const coverageResponse = await getCoverage(
          sourceIdentifier.id,
          coverageDate
        );
        const response = await coverageResponse;

        // Remove from loading state
        setLoadingSourceIdentifiers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sourceIdentifier.id);
          return newSet;
        });

        // Check if the coverage data is empty (all coordinates are zero)
        if (isEmptyCoverage(response.viewBoxCoordinates)) {
          toastRef.current({
            title: messagesRef.current.coverageNoData,
            description: `Source identifier ${sourceIdentifierName} doesn't have any coverage ${formatDateRange(
              coverageDate
            )}.`,
            variant: 'warning',
          });
        }

        // Coverage data exists, add to selection
        setSelectedSourceIdentifiers((prevSelected) => [
          ...prevSelected,
          sourceIdentifier,
        ]);

        toastRef.current({
          title: 'Success!',
          description: `Added coverage for source identifier ${sourceIdentifierName} to the map.`,
        });
      } catch (error) {
        // Remove from loading state on error
        setLoadingSourceIdentifiers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sourceIdentifier.id);
          return newSet;
        });

        console.error(
          `Failed to fetch coverage for ${sourceIdentifier.id}`,
          error
        );
        toastRef.current({
          title: 'Error',
          description: `Failed to load coverage for source identifier ${sourceIdentifierName}. Please try again.`,
          variant: 'destructive',
        });
      }
    },
    [
      permissions.coverage,
      getCoverage,
      coverageDate,
      isEmptyCoverage,
      formatDateRange,
    ]
  );

  useEffect(() => {
    const updateCoverageOverlays = async () => {
      // Don't make coverage API calls if user doesn't have permissions
      if (!permissions.coverage) {
        return;
      }

      // Only set loading state if we have selected source identifiers
      const hasSelectedIdentifiers = selectedSourceIdentifiers.length > 0;
      if (hasSelectedIdentifiers) {
        setIsLoadingPeriodNavigation(true);
      }

      const newOverlays: Record<
        number,
        {
          base64: string;
          imageExtent: number[];
        }
      > = {};

      let hasAnyData = false;
      let hasAllEmptyData = true;

      for (const sourceIdentifier of selectedSourceIdentifiers) {
        if (!coverageDate) {
          continue;
        }

        try {
          const coverageResponse = await getCoverage(
            sourceIdentifier.id,
            coverageDate
          );
          const response = await coverageResponse;

          hasAnyData = true;
          hasAllEmptyData = false;

          // At this point, we know the coverage data should be valid since
          // it was already checked in onSourceIdentifierSelect
          const regex = /\((-?\d+\.\d+)\s(-?\d+\.\d+)\)/g;
          const coordinates: number[] = [];
          let match;

          while ((match = regex.exec(response.viewBoxCoordinates)) !== null) {
            const longitude = parseFloat(match[1]);
            const latitude = parseFloat(match[2]);
            // Transform coordinates from data projection to display projection
            const [x, y] = transform(
              [Number(longitude), Number(latitude)],
              dataProjectionRef.current,
              displayProjectionRef.current
            );
            coordinates.push(x, y);
          }

          // The resulting coordinates array needs to be flattened into an extent
          const imageExtent = [
            coordinates[0],
            coordinates[1],
            coordinates[2],
            coordinates[3],
          ];

          newOverlays[sourceIdentifier.id] = {
            base64: response.image,
            imageExtent,
          };
        } catch (error) {
          console.error(
            `Failed to fetch coverage for ${sourceIdentifier.id}`,
            error
          );
          // If there's an error fetching coverage, remove from selection
          setSelectedSourceIdentifiers((prev) =>
            prev.filter((si) => si.id !== sourceIdentifier.id)
          );
        }
      }

      // Create a unique key for this period to track if we've already warned about it
      const periodKey = coverageDate
        ? `${coverageDate.from?.getTime()}-${coverageDate.to?.getTime()}-${selectedSourceIdentifiers
            .map((si) => si.id)
            .sort()
            .join(',')}`
        : '';

      // Show warning if all selected source identifiers have no data for this period
      // but only if we haven't already warned about this exact period + source identifier combination
      if (
        selectedSourceIdentifiers.length > 0 &&
        hasAllEmptyData &&
        coverageDate &&
        periodKey &&
        !warnedPeriods.has(periodKey)
      ) {
        // Mark this period as warned
        setWarnedPeriods((prev) => new Set(prev).add(periodKey));

        toastRef.current({
          title: messagesRef.current.coverageNoData,
          description: `No coverage data is available for any source identifier ${formatDateRange(
            coverageDate
          )}.`,
          variant: 'warning',
        });
      }

      // Only update if there are changes to avoid re-triggering useEffect
      setCoverageOverlays((prevOverlays) => {
        // Always update with newOverlays to ensure old data is cleared
        // when source identifiers don't have data in the new period
        const prevKeys = Object.keys(prevOverlays);
        const newKeys = Object.keys(newOverlays);

        // Check if the keys are different (source identifiers changed)
        const keysChanged =
          prevKeys.length !== newKeys.length ||
          prevKeys.some((key) => !newKeys.includes(key)) ||
          newKeys.some((key) => !prevKeys.includes(key));

        // Check if any existing overlay data has changed
        const hasDataChanges = newKeys.some(
          (sourceIdentifierId) =>
            !prevOverlays[parseInt(sourceIdentifierId)] ||
            prevOverlays[parseInt(sourceIdentifierId)].base64 !==
              newOverlays[parseInt(sourceIdentifierId)].base64 ||
            JSON.stringify(
              prevOverlays[parseInt(sourceIdentifierId)].imageExtent
            ) !==
              JSON.stringify(
                newOverlays[parseInt(sourceIdentifierId)].imageExtent
              )
        );

        const shouldUpdate = keysChanged || hasDataChanges;

        return shouldUpdate ? newOverlays : prevOverlays;
      });

      // Clear loading state after all requests are complete
      setIsLoadingPeriodNavigation(false);
    };

    updateCoverageOverlays();
  }, [
    selectedSourceIdentifiers,
    getCoverage,
    coverageDate,
    permissions.coverage,
    formatDateRange,
    warnedPeriods,
  ]);

  useEffect(() => {
    const handleRemoveCoverage = (event: Event) => {
      // Type assertion to inform TypeScript that this is a CustomEvent
      const customEvent = event as CustomEvent<{ id: string }>;
      const { id } = customEvent.detail;

      setSelectedSourceIdentifiers((prevSelected) =>
        prevSelected.filter((item) => item.id !== parseInt(id))
      );
    };

    // Attach the event listener
    window.addEventListener('removeCoverage', handleRemoveCoverage);

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener('removeCoverage', handleRemoveCoverage);
    };
  }, []);

  const addSourceIdentifiersToMap = useCallback(
    (addLayer: AddLayer, sourceIdentifiers: SourceIdentifier[],
     displayProjection: string,
    dataProjection: string) => {
      if (!sourceIdentifiers || sourceIdentifiers.length === 0) return null;

      const sourceIdentifierGeoJson: GeoJson = {
        type: 'FeatureCollection',
        properties: {
          id: 'source-identifiers',
          name: 'Source Identifiers',
          icon: 'source-identifier',
        },
        features: sourceIdentifiers.map((sourceIdentifier) => {
          const coordinates = parsePoint(sourceIdentifier.pos);
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates,
            },
            properties: {
              id: sourceIdentifier.id.toString(),
              name:
                sourceIdentifier.pssMmsi === -1
                  ? sourceIdentifier.sourceTag
                  : sourceIdentifier.pssMmsi.toString(),
              pssMmsi: sourceIdentifier.pssMmsi,
              sourceTag: sourceIdentifier.sourceTag,
              countryMid: sourceIdentifier.countryMid,
              pos: sourceIdentifier.pos,
              regionId: sourceIdentifier.regionId,
              covEnabled: sourceIdentifier.covEnabled,
              createdTimeUtc: sourceIdentifier.createdTimeUtc,
              uuid: sourceIdentifier.uuid,
              type: 'source-identifier',
              size: 'medium',
            },
          };
        }),
      };

    const layer = shapeToVectorLayer(
      sourceIdentifierGeoJson,
      displayProjection,
      dataProjection
    );

      addLayer(
        layer,
        'Source identifiers',
        true,
        true,
        true,
        sourceIdentifierGeoJson.properties,
        sourceIdentifierGeoJson.properties.id,
        false,
        true,
        true
      );
    },
    []
  );

  const isSourceIdentifierLoading = useCallback(
    (sourceIdentifierId: number) => {
      return loadingSourceIdentifiers.has(sourceIdentifierId);
    },
    [loadingSourceIdentifiers]
  );

  const setSourceIdentifiersAsSelectedOnStartup = useCallback(
    (sourceIdentifiers: SourceIdentifier[]) => {
      setSelectedSourceIdentifiers(sourceIdentifiers);
    },
    []
  );

  const findSourceIdentifierById = useCallback(
    (id: number) => {
      return sourceIdentifiers.find((si) => si.id === id);
    },
    [sourceIdentifiers]
  );

  const findSourceIdentifierByMmsi = useCallback(
    (mmsi: string) => {
      const mmsiNumber = parseInt(mmsi, 10);
      return sourceIdentifiers.find((si) => si.pssMmsi === mmsiNumber);
    },
    [sourceIdentifiers]
  );

  // Period navigation functions - Define getCurrentPeriodDelta first to avoid circular dependencies
  const getCurrentPeriodDelta = useCallback(() => {
    if (!coverageDate || !coverageDate.from || !coverageDate.to) return 24; // Default to 24 hours

    // Calculate the difference in hours between the current from and to dates
    const diffInMs = coverageDate.to.getTime() - coverageDate.from.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);

    return diffInHours;
  }, [coverageDate]);

  const navigateToNextPeriod = useCallback(() => {
    if (!coverageDate) return;

    // Calculate the current period delta (in hours)
    const currentPeriodDelta = getCurrentPeriodDelta();

    // Navigate to the next period
    setCoverageDate((prev) => {
      if (!prev?.from || !prev?.to) return prev;

      const newFrom = new Date(
        prev.from.getTime() + currentPeriodDelta * 60 * 60 * 1000
      );
      const newTo = new Date(
        prev.to.getTime() + currentPeriodDelta * 60 * 60 * 1000
      );

      return {
        from: newFrom,
        to: newTo,
      };
    });
  }, [coverageDate, getCurrentPeriodDelta]);

  const navigateToPreviousPeriod = useCallback(() => {
    if (!coverageDate) return;

    // Calculate the current period delta (in hours)
    const currentPeriodDelta = getCurrentPeriodDelta();

    // Navigate to the previous period
    setCoverageDate((prev) => {
      if (!prev?.from || !prev?.to) return prev;

      const newFrom = new Date(
        prev.from.getTime() - currentPeriodDelta * 60 * 60 * 1000
      );
      const newTo = new Date(
        prev.to.getTime() - currentPeriodDelta * 60 * 60 * 1000
      );

      return {
        from: newFrom,
        to: newTo,
      };
    });
  }, [coverageDate, getCurrentPeriodDelta]);

  const getNextPeriodDateRange = useCallback(() => {
    if (!coverageDate || !coverageDate.to) return undefined;

    const currentPeriodDelta = getCurrentPeriodDelta();

    return {
      from: new Date(coverageDate.to.getTime()),
      to: new Date(
        coverageDate.to.getTime() + currentPeriodDelta * 60 * 60 * 1000
      ),
    };
  }, [coverageDate, getCurrentPeriodDelta]);

  const getPreviousPeriodDateRange = useCallback(() => {
    if (!coverageDate || !coverageDate.from) return undefined;

    const currentPeriodDelta = getCurrentPeriodDelta();

    return {
      from: new Date(
        coverageDate.from.getTime() - currentPeriodDelta * 60 * 60 * 1000
      ),
      to: new Date(coverageDate.from.getTime()),
    };
  }, [coverageDate, getCurrentPeriodDelta]);

  // Helper function to check if navigating to next period would go into the future
  const wouldNextPeriodBeInFuture = useCallback(() => {
    const nextPeriod = getNextPeriodDateRange();
    if (!nextPeriod) return false;

    const now = new Date();
    return nextPeriod.from.getTime() > now.getTime();
  }, [getNextPeriodDateRange]);

  const value = useMemo(
    () => ({
      selectedSourceIdentifiers,
      setSelectedSourceIdentifiers,
      onSourceIdentifierSelect,
      coverageDate,
      setCoverageDate,
      coverageOverlays,
      addSourceIdentifiersToMap,
      setSourceIdentifiersAsSelectedOnStartup,
      coverageLegend,
      showCoverageLegend,
      loadingSourceIdentifiers,
      isSourceIdentifierLoading,
      allSourceIdentifiers: sourceIdentifiers, // Use sourceIdentifiers which is always an array
      isLoadingAllSourceIdentifiers,
      findSourceIdentifierById,
      findSourceIdentifierByMmsi,
      // Period navigation functions
      navigateToNextPeriod,
      navigateToPreviousPeriod,
      getCurrentPeriodDelta,
      getNextPeriodDateRange,
      getPreviousPeriodDateRange,
      isLoadingPeriodNavigation,
      wouldNextPeriodBeInFuture,
    }),
    [
      selectedSourceIdentifiers,
      coverageDate,
      coverageOverlays,
      coverageLegend,
      showCoverageLegend,
      loadingSourceIdentifiers,
      sourceIdentifiers,
      isLoadingAllSourceIdentifiers,
    ]
  );

  return (
    <CoverageContext.Provider value={value}>
      {children}
    </CoverageContext.Provider>
  );
};

export const useCoverage = () => {
  const context = useContext(CoverageContext);
  if (!context) {
    throw new Error(
      'useCoverage must be used within a CoverageContextProvider'
    );
  }
  return context;
};
